import { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface SegmentOverviewCardProps {
  narrative?: string;
  commercialAction?: string;
  gapAnalysis?: string | Record<string, unknown>;
  /** When true, shows the edit pencil. Controlled by parent. */
  editMode?: boolean;
  /** When provided, enables inline editing. Called with the updated fields. */
  onSave?: (fields: { narrative?: string; commercialAction?: string; gapAnalysis?: string | Record<string, unknown> }) => Promise<void>;
}

export default function SegmentOverviewCard({ narrative, commercialAction, gapAnalysis, editMode, onSave }: SegmentOverviewCardProps): React.JSX.Element | null {
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Draft state for edited values
  const gapStr = typeof gapAnalysis === 'string' ? gapAnalysis : gapAnalysis ? JSON.stringify(gapAnalysis, null, 2) : '';
  const [draftNarrative, setDraftNarrative] = useState(narrative ?? '');
  const [draftCommercial, setDraftCommercial] = useState(commercialAction ?? '');
  const [draftGap, setDraftGap] = useState(gapStr);

  const handleEdit = useCallback(() => {
    setDraftNarrative(narrative ?? '');
    setDraftCommercial(commercialAction ?? '');
    setDraftGap(typeof gapAnalysis === 'string' ? gapAnalysis : gapAnalysis ? JSON.stringify(gapAnalysis, null, 2) : '');
    setEditing(true);
  }, [narrative, commercialAction, gapAnalysis]);

  const handleCancel = useCallback(() => {
    setEditing(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (!onSave) return;
    setSaving(true);
    try {
      // Try to parse gap analysis back to an object if it looks like JSON,
      // so we don't save a stringified JSON blob that then renders as raw JSON.
      let parsedGap: string | Record<string, unknown> | undefined = draftGap || undefined;
      if (typeof parsedGap === 'string') {
        const trimmed = parsedGap.trim();
        if (trimmed.startsWith('{')) {
          try { parsedGap = JSON.parse(trimmed) as Record<string, unknown>; } catch { /* keep as string */ }
        }
      }
      await onSave({
        narrative: draftNarrative || undefined,
        commercialAction: draftCommercial || undefined,
        gapAnalysis: parsedGap,
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }, [onSave, draftNarrative, draftCommercial, draftGap]);

  if (!narrative && !commercialAction && !gapAnalysis) return null;

  return (
    <Root>
      <Header onClick={() => !editing && setExpanded(!expanded)}>
        <span>Segment Overview</span>
        <HeaderActions>
          {onSave && editMode && !editing && (
            <EditButton
              onClick={(e) => {
                e.stopPropagation();
                handleEdit();
              }}
              title="Edit overview"
            >
              <EditIcon fontSize="small" />
            </EditButton>
          )}
          {editing && (
            <>
              <SaveButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleSave();
                }}
                disabled={saving}
                title="Save changes"
              >
                <CheckIcon fontSize="small" />
              </SaveButton>
              <CancelButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancel();
                }}
                disabled={saving}
                title="Cancel editing"
              >
                <CloseIcon fontSize="small" />
              </CancelButton>
            </>
          )}
          {!editing && (
            <ExpandIcon $expanded={expanded}><ExpandMoreIcon /></ExpandIcon>
          )}
        </HeaderActions>
      </Header>
      {expanded && (
        <Content>
          {editing ? (
            <>
              <FieldLabel>Narrative</FieldLabel>
              <EditTextarea
                value={draftNarrative}
                onChange={(e) => setDraftNarrative(e.target.value)}
                rows={5}
                disabled={saving}
              />
              <CardRow>
                <ActionCard>
                  <CardLabel style={{ color: '#2d7a2e' }}>Commercial Action</CardLabel>
                  <EditTextarea
                    value={draftCommercial}
                    onChange={(e) => setDraftCommercial(e.target.value)}
                    rows={3}
                    disabled={saving}
                  />
                </ActionCard>
                <GapCard>
                  <CardLabel style={{ color: '#92400e' }}>Data Gaps</CardLabel>
                  <EditTextarea
                    value={draftGap}
                    onChange={(e) => setDraftGap(e.target.value)}
                    rows={3}
                    disabled={saving}
                  />
                </GapCard>
              </CardRow>
            </>
          ) : (
            <>
              {narrative && (
                <Narrative>{narrative}</Narrative>
              )}
              {(commercialAction || gapAnalysis) && (
                <CardRow>
                  {commercialAction && (
                    <ActionCard>
                      <CardLabel style={{ color: '#2d7a2e' }}>Commercial Action</CardLabel>
                      {commercialAction}
                    </ActionCard>
                  )}
                  {gapAnalysis && (
                    <GapCard>
                      <CardLabel style={{ color: '#92400e' }}>Data Gaps</CardLabel>
                      {typeof gapAnalysis === 'string'
                        ? gapAnalysis
                        : Object.entries(gapAnalysis).map(([k, v]) => (
                            <div key={k}><strong style={{ textTransform: 'capitalize' }}>{k}:</strong> {String(v)}</div>
                          ))
                      }
                    </GapCard>
                  )}
                </CardRow>
              )}
            </>
          )}
        </Content>
      )}
    </Root>
  );
}

const Root = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
`;

const Header = styled.div`
  padding: 8px 16px;
  font-size: 20px;
  font-weight: 700;
  font-family: var(--font-bold, inherit);
  color: var(--black-l, #323232);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;
  min-height: 64px;
  box-sizing: border-box;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const IconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  background: transparent;
  color: rgba(0, 0, 0, 0.54);
  transition: background 120ms ease;
  &:hover:not(:disabled) { background: rgba(0, 0, 0, 0.06); }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const EditButton = styled(IconBtn)``;
const SaveButton = styled(IconBtn)`
  color: #2d7a2e;
  &:hover:not(:disabled) { background: #e3f9e8; }
`;
const CancelButton = styled(IconBtn)`
  color: #991b1b;
  &:hover:not(:disabled) { background: #fef2f2; }
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  color: rgba(0, 0, 0, 0.54);
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(${p => p.$expanded ? '180deg' : '0deg'});
  display: flex;
`;

const Content = styled.div`
  padding: 0 16px 16px;
`;

const Narrative = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #4c4c4c;
  margin-bottom: 16px;
`;

const CardRow = styled.div`
  display: flex;
  gap: 16px;
`;

const ActionCard = styled.div`
  flex: 1;
  border-radius: 8px;
  padding: 14px;
  background: #e3f9e8;
  border: 1px solid #4CB847;
  font-size: 13px;
  line-height: 1.5;
  color: #4c4c4c;
`;

const GapCard = styled.div`
  flex: 1;
  border-radius: 8px;
  padding: 14px;
  background: #fffbeb;
  border: 1px solid #f59e0b;
  font-size: 13px;
  line-height: 1.5;
  color: #4c4c4c;
`;

const CardLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;

const FieldLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  color: #626262;
`;

const EditTextarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.5;
  font-family: inherit;
  color: #323232;
  resize: vertical;
  margin-bottom: 12px;
  &:focus {
    outline: none;
    border-color: #1976d2;
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.15);
  }
  &:disabled { opacity: 0.6; }
`;
